import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `members` domain — the member list, the two orphaned
 * sub-pages, and the six tabs of the member drawer.
 *
 * Two things here were verified against the services rather than the screen,
 * because the screen says something else:
 *
 *  - Suspend, Blacklist and Delete are ONE action. `setStatus`
 *    (apps/member/src/repo/members.ts) branches only on `active` vs
 *    not-`active`: all three suspend the login credential and revoke every
 *    session. Only the badge, the outbox event and a `member_blacklisted`
 *    alert differ, and all three are undone by Activate — except that Delete
 *    also stamps `deleted_at`, which Activate never clears, so a re-activated
 *    member can log in but no longer appears in this list.
 *  - `netDifference` is PLAYER-perspective (`payout − stake`,
 *    apps/bet/src/routes/bets.ts). Green means the player is up and the house
 *    is down. Risk → Exposure shows the same money from the house side, so the
 *    colours mean opposite things on the two screens; both `pos`/`neg` pairs
 *    say so explicitly.
 */
export const membersGuide: DomainGuide = {
  domain: 'members',
  pages: [
    {
      id: 'members.list',
      route: '/members',
      permission: 'member:read',
      status: 'live',
      fields: [
        { key: 'username', kind: 'text' },
        { key: 'agent', kind: 'text' },
        { key: 'status', kind: 'status' },
        { key: 'membershipLevel', kind: 'count' },
        { key: 'totalDeposit', kind: 'money-unsigned' },
        { key: 'totalTurnover', kind: 'money-unsigned' },
        { key: 'currentBalance', kind: 'money-unsigned' },
        { key: 'totalWithdraw', kind: 'money-unsigned' },
        { key: 'totalBonus', kind: 'money-unsigned' },
        {
          key: 'netDifference',
          kind: 'money-signed',
          formula: 'netDifference = payout − stake (player perspective, lifetime)',
        },
        { key: 'referralCode', kind: 'text' },
        { key: 'lastLogin', kind: 'date' },
      ],
      cautionCount: 7,
      warningCount: 1,
    },
    {
      id: 'members.groups',
      route: '/members/groups',
      permission: 'member:read',
      status: 'live',
      fields: [
        { key: 'code', kind: 'text' },
        { key: 'name', kind: 'text' },
        { key: 'description', kind: 'text' },
        { key: 'color', kind: 'text' },
        { key: 'system', kind: 'status' },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
    {
      id: 'members.blacklist',
      route: '/members/blacklist',
      permission: 'member:update',
      status: 'partially-wired',
      fields: [
        { key: 'kind', kind: 'status' },
        { key: 'value', kind: 'text' },
        { key: 'reason', kind: 'text' },
        { key: 'expiresAt', kind: 'date' },
        { key: 'createdAt', kind: 'date' },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
    {
      id: 'members.detailInfo',
      route: '/members → member drawer → Info',
      permission: 'member:read',
      status: 'live',
      fields: [
        { key: 'status', kind: 'status' },
        { key: 'phone', kind: 'text' },
        { key: 'email', kind: 'text' },
        { key: 'name', kind: 'text' },
        { key: 'lineId', kind: 'text' },
        { key: 'referralCode', kind: 'text' },
        { key: 'registeredAt', kind: 'date' },
        { key: 'birthDate', kind: 'date' },
        { key: 'registeredIp', kind: 'text' },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
    {
      id: 'members.detailBanks',
      route: '/members → member drawer → Bank Accounts',
      permission: 'member:read',
      status: 'live',
      fields: [
        { key: 'bankAccount', kind: 'text' },
        { key: 'accountName', kind: 'text' },
        { key: 'primary', kind: 'status' },
        { key: 'verified', kind: 'status' },
      ],
      cautionCount: 5,
      warningCount: 1,
    },
    {
      id: 'members.detailGroups',
      route: '/members → member drawer → Groups',
      permission: 'member:read',
      status: 'live',
      fields: [
        { key: 'groupName', kind: 'text' },
        { key: 'groupCode', kind: 'text' },
      ],
      cautionCount: 3,
      warningCount: 0,
    },
    {
      id: 'members.detailPromotion',
      route: '/members → member drawer → Promotion',
      permission: 'promotion:read',
      status: 'live',
      fields: [
        { key: 'promotionTitle', kind: 'text' },
        { key: 'claimStatus', kind: 'status' },
        { key: 'grantedAmount', kind: 'money-unsigned' },
        {
          key: 'turnoverProgress',
          kind: 'percent',
          formula: 'progress = turnoverAchieved ÷ turnoverRequired',
        },
        { key: 'expiresAt', kind: 'date' },
      ],
      cautionCount: 5,
      warningCount: 3,
    },
    {
      id: 'members.detailWallet',
      route: '/members → member drawer → Wallet',
      permission: 'wallet:read',
      status: 'live',
      fields: [
        { key: 'creditCash', kind: 'money-unsigned' },
        { key: 'creditPromotion', kind: 'money-unsigned' },
        {
          key: 'balance',
          kind: 'money-unsigned',
          formula: 'balance = cash + promotion + commission + cashback',
        },
        {
          key: 'withdrawable',
          kind: 'money-unsigned',
          formula: 'withdrawable = cash   (commission must be transferred to cash first)',
        },
      ],
      cautionCount: 4,
      warningCount: 1,
    },
    {
      id: 'members.detailActions',
      route: '/members → member drawer → Actions',
      // Two codes, because the reset control on this tab answers to a narrower
      // one than the tab: `member:update` opens suspend, blacklist and the 2FA
      // reset, and the password reset is `member:reset_password` alone.
      permission: 'member:update + member:reset_password',
      status: 'live',
      fields: [
        { key: 'accountStatus', kind: 'status' },
        { key: 'resetPassword', kind: 'text' },
        { key: 'resetMfa', kind: 'text' },
        { key: 'loginAsCustomer', kind: 'text' },
      ],
      cautionCount: 4,
      warningCount: 3,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
